/**
 * Trigger a browser download of the user-audit CSV from
 * `GET /api/audit/export`.
 *
 * Why a fetch + blob rather than `window.location = '/api/audit/export'`:
 * the endpoint is auth-gated, and the Bearer token only rides on `fetch`
 * (the interceptor in `utils/api.ts` injects it for `/api/*`). A top-level
 * navigation carries no token and would 401. So we fetch the CSV with the
 * token attached, wrap the body in an object URL, and click a synthetic
 * anchor to save it.
 *
 * Pass `userId` to filter the export to a single user; omit it to export
 * the whole table. Throws on a non-2xx response so the caller can surface
 * an error (a 401 also trips the interceptor's re-login redirect).
 */
export async function downloadUserAuditCsv(
    options: { userId?: string } = {},
): Promise<void> {
    const query = options.userId
        ? `?user_id=${encodeURIComponent(options.userId)}`
        : '';

    const response = await fetch(`/api/audit/export${query}`);
    if (!response.ok) {
        throw new Error(
            `Audit export failed: ${response.status} ${response.statusText}`,
        );
    }

    const blob = await response.blob();
    const filename =
        filenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
        ) ?? defaultFilename(options.userId);

    const objectUrl = URL.createObjectURL(blob);
    try {
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}


function defaultFilename(userId?: string): string {
    return userId ? `user_audit_${userId}.csv` : 'user_audit.csv';
}


/**
 * Pull the `filename="..."` out of a Content-Disposition header so the
 * saved file matches what the backend named it. Returns null when the
 * header is absent or has no filename, letting the caller fall back.
 */
function filenameFromContentDisposition(header: string | null): string | null {
    if (!header) return null;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match ? decodeURIComponent(match[1]) : null;
}
